import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FORM_FIELD_PATHS } from '../../constants';
import { useCompanyForm } from '../../hooks/useCompanyForm';
import { useNavigate } from 'react-router-dom';
import walkthroughIds from 'pages/organisations/walkthroughIds';
import {
    Box,
    Checkbox as ChakraCheckbox,
    Skeleton,
    Divider,
    HStack,
    Icon,
    SkeletonCircle,
    Spinner,
    Stack,
    VStack,
    useDisclosure,
} from '@chakra-ui/react';

import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from 'components/v4';
import ChakraButton from 'components/v4/Button';
import Grid from 'components/v4/Grid';
import { useQueryClient } from '@tanstack/react-query';
import { axios } from 'services/axios';
import { refreshNavigationLists } from 'components/pickers/navigationCache';
import { toast } from 'react-toastify';
import { MdCancel, MdCheckCircle, MdPending } from 'react-icons/md';

const EntityDangerzone = ({ darkMode, isCompany, resetToMainTable }) => {
    // Taken from old Edit Company page
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [modalLoading, setModalLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { formData, updateFormData } = useCompanyForm();
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    const toastId = useRef(null);
    const [userNumberState, setUserNumberState] = useState({
        deallocate: false,
        isDisabled: true,
        showIcon: true,
        showButton: true,
        isLoading: false,
        count: '0',
    });
    const [userData, setUserData] = useState([]);

    const [numberBlockState, setNumberBlockState] = useState({
        deallocate: false,
        isDisabled: true,
        showIcon: true,
        showButton: true,
        isLoading: false,
        count: '0',
    });

    const [nbData, setNBData] = useState([]);

    const [numberTrunkState, setNumberTrunkState] = useState({
        deallocate: false,
        isDisabled: true,
        showIcon: true,
        showPending: false,
        showButton: true,
        isLoading: false,
        count: '0',
    });
    const [numberTrunkData, setNumberTrunkData] = useState([]);
    const [trunkState, setTrunkState] = useState({
        deallocate: false,
        isDisabled: true,
        showIcon: true,
        showPending: false,
        showButton: true,
        isLoading: false,
        count: '0',
    });
    const [trunkData, setTrunkData] = useState([]);

    const [deactivateState, setDeactivateState] = useState({
        deactivate: false,
        isDisabled: true,
        showIcon: true,
        showButton: true,
        isLoading: false,
    });
    const [deleteState, setDeleteState] = useState({
        delete: false,
        isDisabled: true,
        isLoading: false,
    });

    const errorToast = (message) => {
        if (!toast.isActive(toastId.current)) {
            toastId.current = toast.error(message);
        }
    };

    const successToast = (message) => {
        if (!toast.isActive(toastId.current)) {
            toastId.current = toast.success(message);
        }
    };

    const { ufDeleteButton, trialOnHoldCheckbox } =
        walkthroughIds.companies.admin;

    const viewNavigate = (route) => {
        resetToMainTable();
        dispatch({
            type: 'CHANGE_CURRENT_COMPANY',
            payload: formData?.id,
        });
        dispatch({
            type: 'CHANGE_CURRENT_PARTNER',
            payload: formData?.partnerID || formData?.id,
        });

        // get parent menu item Accounts
        let acc = document.querySelector('.Accounts');
        // if only 1 then the menu item is closed
        if (acc.children.length == 1) {
            var divElement = acc.querySelector('div');
            // create on click event
            const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            divElement.dispatchEvent(clickEvent);
        }

        // redirect is to select the sub menu item
        // scope 20 to focus on currently managing company
        dispatch({
            type: 'REDIRECT',
            payload: {
                pathname:
                    '/accounts' +
                    (route === '/resource-accounts' ? '/Users' : route),
                scope: 20,
            },
        });

        navigate(route);
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    };

    const onDeallocateUsers = async () => {
        if (userData?.length > 0) {
            setUserNumberState({ ...userNumberState, isLoading: true });
            for (var item of userData) {
                let numId = item.userProfiles[0].numberID;
                let platformId = item.userProfiles[0].platformID;
                let platformUserId = item.userProfiles[0].platformUserID;
                try {
                    await axios.delete(
                        `/UserProfile?NumberID=${numId}&PlatformID=${platformId}&PlatformUserID=${platformUserId}`,
                    );
                } catch (e) {
                    console.log(e);
                    errorToast(e.response.data.message);
                    setUserNumberState({
                        ...userNumberState,
                        isLoading: false,
                    });
                    return;
                }
            }
            setUserNumberState({
                ...userNumberState,
                isLoading: false,
                showButton: false,
                showIcon: false,
                count: `${userData.length}`,
            });
            if (numberTrunkData?.length > 0) {
                setNumberTrunkState({
                    ...numberTrunkState,
                    deallocate: true,
                    isDisabled: false,
                });
                return;
            }
            // nb assigned if any
            if (nbData?.length > 0) {
                setNumberBlockState({
                    ...numberBlockState,
                    deallocate: true,
                    isDisabled: false,
                });
                return;
            }
            // trunks exists if any
            if (trunkData?.length > 0) {
                setTrunkState({
                    ...trunkState,
                    deallocate: true,
                    isDisabled: false,
                });
                return;
            }
            // state of company
            if (formData?.isActive) {
                setDeactivateState({
                    ...deactivateState,
                    deallocate: true,
                    isDisabled: false,
                });
                return;
            }
            // reaches here means all good, can delete company
            else {
                setDeleteState({
                    ...deleteState,
                    delete: true,
                    isDisabled: false,
                });
            }
        }
    };

    const onDeleteHandler = async () => {
        setModalLoading(true);
        onOpen();
        try {
            // Fetch all numbers and trunks as before
            const res0 = await axios.get(`/Company/${formData?.id}/Numbers`);
            const res3 = await axios.get(`/trunks/${formData?.id}`);
            // Fetch available platforms
            const platformsRes = await axios.get(
                `/Company/${formData?.id}/Platforms/Available`,
            );
            const platforms = platformsRes.data || [];
            // Fetch users and resource accounts for each platform
            let userDataArr = [];
            let numberTrunkStateArr = [];
            for (const platform of platforms) {
                // Users
                let filtered = [];
                try {
                    const res2 = await axios.get(
                        `/PlatformUsers/${formData?.id}/Platform/${platform.id}`,
                    );
                    if (Array.isArray(res2.data.platformUsers)) {
                        filtered = res2.data.platformUsers.filter(
                            (v) =>
                                Array.isArray(v.userProfiles) &&
                                v.userProfiles.length > 0 &&
                                v.userProfiles[0].numberID,
                        );
                    }
                } catch (err) {
                    // Optionally log or handle error for user fetch
                }
                userDataArr.push(...filtered);
            }
            for (const platform of platforms) {
                // Number Trunks
                let filteredTrunks = [];
                try {
                    const resTrunks = await axios.get(
                        `/company/${formData?.id}/PlatformNumbers/${platform.id}`,
                    );
                    if (Array.isArray(resTrunks.data.platformNumbers)) {
                        filteredTrunks = resTrunks.data.platformNumbers.filter(
                            (v) => v.trunkID,
                        );
                    }
                } catch (err) {
                    toast.error(
                        `Error fetching trunks for platform ${platform.id}: ${err.message}`,
                    );
                }
                numberTrunkStateArr.push(...filteredTrunks);
            }

            const numbersResponse = res0?.data?.numbers?.map(
                (v) => v.numberBlockID,
            );
            const trunksResponse = res3?.data?.filter(
                (v) =>
                    v.trunkTypeID === '0b75dae1-43e3-4f09-add8-09ce2f86b2f6' ||
                    v.trunkTypeID === 'f4de8d22-bf78-4c05-a677-4692dfc5444f',
            );
            let uniqueNumberBlockIDs = [...new Set(numbersResponse)];
            // user handling
            if (userDataArr.length > 0) {
                setUserNumberState({
                    ...userNumberState,
                    deallocate: true,
                    count: `${userDataArr.length}`,
                    showButton: true,
                    showIcon: true,
                    isDisabled: false,
                });
                setUserData(userDataArr);
            } else {
                setUserNumberState({
                    ...userNumberState,
                    deallocate: false,
                    count: '0',
                    showButton: false,
                    showIcon: false,
                });
            }
            if (numberTrunkStateArr.length > 0) {
                setNumberTrunkState({
                    ...numberTrunkState,
                    deallocate: userDataArr.length > 0 ? false : true,
                    count: numberTrunkStateArr.length,
                    showButton: true,
                    showIcon: true,
                    isDisabled: userDataArr.length > 0 ? true : false,
                });
                setNumberTrunkData(numberTrunkStateArr);
            } else {
                setNumberTrunkState({
                    ...numberTrunkState,
                    deallocate: false,
                    count: '0',
                    showButton: false,
                    showIcon: false,
                });
            }
            // nb handling
            if (uniqueNumberBlockIDs.length > 0) {
                setNumberBlockState({
                    ...numberBlockState,
                    deallocate:
                        userDataArr.length > 0 || numberTrunkStateArr.length > 0
                            ? false
                            : true,
                    count: uniqueNumberBlockIDs.length,
                    showButton: true,
                    showIcon: true,
                    isDisabled:
                        userDataArr.length > 0 || numberTrunkStateArr.length > 0
                            ? true
                            : false,
                });
                setNBData(uniqueNumberBlockIDs);
            } else {
                setNumberBlockState({
                    ...numberBlockState,
                    deallocate: false,
                    count: res0?.data?.length,
                    showButton: false,
                    showIcon: false,
                });
            }

            // trunk handling (same as before)
            if (trunksResponse.length > 0) {
                if (trunksResponse.every((a) => a.status === 9)) {
                    setTrunkState({
                        ...trunkState,
                        deallocate:
                            userDataArr.length > 0 ||
                            numberTrunkStateArr.length > 0 ||
                            uniqueNumberBlockIDs.length > 0
                                ? false
                                : true,
                        count: trunksResponse.length,
                        showButton: false,
                        showIcon: false,
                        showPending: true,
                        isDisabled:
                            userDataArr.length > 0 ||
                            numberTrunkStateArr.length > 0 ||
                            uniqueNumberBlockIDs.length > 0
                                ? true
                                : false,
                    });
                } else {
                    setTrunkState({
                        ...trunkState,
                        deallocate:
                            userDataArr.length > 0 ||
                            numberTrunkStateArr.length > 0 ||
                            uniqueNumberBlockIDs.length > 0
                                ? false
                                : true,
                        count: trunksResponse.length,
                        showButton: true,
                        showIcon: true,
                        showPending: false,
                        isDisabled:
                            userDataArr.length > 0 ||
                            numberTrunkStateArr.length > 0 ||
                            uniqueNumberBlockIDs.length > 0
                                ? true
                                : false,
                    });
                }
                setTrunkData(trunksResponse);
            } else {
                setTrunkState({
                    ...trunkState,
                    deallocate: false,
                    count: '0',
                    showButton: false,
                    showIcon: false,
                });
            }
            if (
                userDataArr.length > 0 ||
                uniqueNumberBlockIDs.length > 0 ||
                trunksResponse.length > 0
            ) {
                /* empty */
            } else {
                setDeactivateState({
                    ...deactivateState,
                    showButton: false,
                    showIcon: false,
                });
                setDeleteState({
                    ...deleteState,
                    delete: true,
                    isDisabled: false,
                });
            }
        } catch (e) {
            console.log(e);
            errorToast(e.response?.data?.message || e.message);
        } finally {
            setModalLoading(false);
        }
    };

    const onCompanyDeleteHandler = async () => {
        setDeleteState({ ...deleteState, isLoading: true });
        if (formData?.isActive) {
            formData.isActive = false;
            try {
                await axios.put('/company', formData);
            } catch (e) {
                errorToast(e.response.data.message);
                setDeactivateState({ ...deactivateState, isLoading: false });
                return;
            }
            // update local formData with new value
            dispatch({
                type: 'CHANGE_MODAL_STATE',
                payload: {
                    isActive: false,
                },
            });
        }
        axios
            .delete(`/company/${formData?.id}`)
            .then(() => {
                refreshNavigationLists(queryClient);
                navigate('/companies');
                resetToMainTable();
                if (currentCompany == formData?.id) {
                    dispatch({
                        type: 'CHANGE_CURRENT_COMPANY',
                        payload: formData?.partnerID,
                    });
                    window.location.reload();
                }
                setTimeout(() => {
                    successToast('Company deleted successfully!');
                }, 1000);
            })
            .catch((err) => {
                errorToast(err.response.data.message);
                setDeleteState({ ...deleteState, isLoading: false });
                return;
            });
    };

    const onDeallocateNumberTrunk = async () => {
        if (numberTrunkData?.length > 0) {
            setNumberTrunkState({ ...numberTrunkState, isLoading: true });
            for (const numbers of numberTrunkData) {
                console.log(numbers);
                try {
                    let res = numbers;
                    res.trunkID = null;
                    // unassign nb from company by setting company id to null
                    await axios.put('/PlatformNumber', res);
                } catch (e) {
                    errorToast(
                        e?.response?.data?.message ||
                            `Unable to remove trunk ${numbers.name}`,
                    );
                    setNumberTrunkState({
                        ...numberTrunkState,
                        isLoading: false,
                    });
                    //isError = true;
                    return;
                }
            }

            setNumberTrunkState({
                ...numberTrunkState,
                isLoading: false,
                showButton: false,
                showIcon: false,
                showPending: false,
                count: `${numberTrunkState.length}`,
            });
            //}

            if (nbData?.length > 0) {
                setNumberBlockState({
                    ...numberBlockState,
                    deallocate: true,
                    isDisabled: false,
                });
                return;
            }
            if (trunkData?.length > 0) {
                setTrunkState({
                    ...trunkState,
                    deallocate: true,
                    isDisabled: false,
                });
                return;
            }
            // checks state of company on success
            if (formData?.isActive) {
                setDeactivateState({
                    ...deactivateState,
                    deallocate: true,
                    isDisabled: false,
                });
                return;
            }
            // should be good to allow delete of company
            else {
                setDeleteState({
                    ...deleteState,
                    delete: true,
                    isDisabled: false,
                });
            }
        }
    };
    const onDeallocateNB = async () => {
        if (nbData?.length > 0) {
            setNumberBlockState({ ...numberBlockState, isLoading: true });
            for (const nbID of nbData) {
                try {
                    // get nb object
                    let res = await axios.get(`NumberBlock/${nbID}`);
                    res.data.companyID = null;
                    // unassign nb from company by setting company id to null
                    await axios.put('/NumberBlock', res.data);
                } catch (e) {
                    errorToast(e.response.data.message);
                    setNumberBlockState({
                        ...numberBlockState,
                        isLoading: false,
                    });
                    return;
                }
            }
            setNumberBlockState({
                ...numberBlockState,
                isLoading: false,
                showButton: false,
                showIcon: false,
                count: `${nbData.length}`,
            });
            // trunks exists if any
            if (trunkData?.length > 0) {
                setTrunkState({
                    ...trunkState,
                    deallocate: true,
                    isDisabled: false,
                });
                return;
            }
            if (trunkData?.length === 0) {
                setDeleteState({
                    ...deleteState,
                    delete: true,
                    isDisabled: false,
                });
            }
        }
    };
    const onDeallocateTrunk = async () => {
        if (trunkData?.length > 0) {
            setTrunkState({ ...trunkState, isLoading: true });
            // console.log('trunkData', trunkData);
            for (const [index, trunk] of trunkData.entries()) {
                try {
                    await axios.delete(`Trunk/${trunk.id}`);
                } catch (e) {
                    errorToast(
                        e?.response?.data?.message ||
                            `Unable to remove trunk ${trunk.name}`,
                    );
                    setTrunkState({
                        ...trunkState,
                        isLoading: false,
                    });
                } finally {
                    if (index === trunkData.length - 1) {
                        // console.log('this is last index');
                        setTrunkState({
                            ...trunkState,
                            isLoading: false,
                        });
                    }
                }
            }

            setTrunkState({
                ...trunkState,
                isLoading: false,
                showButton: false,
                showIcon: false,
                count: `${trunkData.length}`,
            });

            setDeleteState({
                ...deleteState,
                delete: true,
                isDisabled: false,
            });
        }
    };

    return (
        <>
            <Box
                sx={{
                    border: '2px solid red',
                    borderRadius: '5px',
                    padding: '5px 15px 5px 15px',
                    backgroundColor: darkMode ? 'dark.bgDark' : '#fff',
                    textColor: darkMode ? 'white' : 'inherit',
                    marginBottom: '25px',
                    '& > hr': {
                        borderColor: 'rgba(0, 0, 0, 0.12)',
                        margin: '10px auto',
                    },
                }}>
                <Grid columns={5}>
                    <Box gridColumn="span 4">
                        <p>
                            <b>Delete</b>
                        </p>
                        <p>
                            By deleting a company they will stop syncing,
                            invoices will change to finalizing and no further
                            billing will occur
                        </p>
                    </Box>

                    <Box textAlign="right" alignContent="center" my="auto">
                        <ChakraButton
                            colorScheme="red"
                            _hover={{
                                bg: 'red.600',
                            }}
                            bg="red.500"
                            onClick={onDeleteHandler}
                            _focus={{
                                boxShadow: '0 0 0 3px rgba(225, 66, 87, 0.6)',
                            }}
                            data-walkthroughid={ufDeleteButton}>
                            Delete
                        </ChakraButton>
                    </Box>
                </Grid>
                {isCompany && formData.isTrial === true && (
                    <>
                        <Divider
                            bg={darkMode ? 'white' : 'inherit'}
                            borderColor="gray.500 !important"
                        />
                        <Grid columns={5}>
                            <Box gridColumn="span 4">
                                <p>
                                    <b> Trial on Hold </b>
                                </p>
                                <p>Disables Inbound &amp; Outbound Calls</p>{' '}
                            </Box>
                            <Box
                                textAlign="right"
                                alignContent="center"
                                my="auto">
                                <ChakraCheckbox
                                    size="lg"
                                    colorScheme="secondary"
                                    isChecked={formData.trialHold}
                                    onChange={() => {
                                        updateFormData(
                                            FORM_FIELD_PATHS.TRIAL_HOLD,
                                            !formData.trialHold,
                                        );
                                    }}
                                    data-walkthroughid={trialOnHoldCheckbox}
                                />
                            </Box>
                        </Grid>
                    </>
                )}
            </Box>

            {/* Delete modal  */}
            <Modal
                autoFocus={false}
                closeOnOverlayClick={false}
                isOpen={isOpen}
                onClose={onClose}
                isCentered>
                <ModalOverlay />
                <ModalContent minW="40%">
                    <ModalHeader>Delete Company: {formData?.name} </ModalHeader>
                    <ModalBody>
                        {modalLoading ? (
                            <Stack>
                                {Array(5)
                                    .fill(1)
                                    .map((_, index) => (
                                        <HStack key={index}>
                                            <SkeletonCircle h={8} />
                                            <Skeleton flex={5} h={8} />
                                            <Skeleton flex={1} h={8} />
                                            <Skeleton flex={1} h={8} />
                                        </HStack>
                                    ))}
                            </Stack>
                        ) : (
                            <Stack>
                                <HStack
                                    display="flex"
                                    justifyContent="space-between">
                                    <HStack>
                                        <VStack>
                                            <Box w={8} h={8}>
                                                {!userNumberState.showIcon ? (
                                                    <Icon
                                                        as={MdCheckCircle}
                                                        w={8}
                                                        h={8}
                                                        color="green.400"
                                                    />
                                                ) : (
                                                    <Icon
                                                        as={MdCancel}
                                                        w={8}
                                                        h={8}
                                                        color="red.400"
                                                    />
                                                )}
                                            </Box>
                                            <Box w={8} h={8}>
                                                {!numberTrunkState.showButton &&
                                                !numberTrunkState.showPending ? (
                                                    <Icon
                                                        as={MdCheckCircle}
                                                        w={8}
                                                        h={8}
                                                        color="green.400"
                                                    />
                                                ) : numberTrunkState.showPending ? (
                                                    <Icon
                                                        as={MdPending}
                                                        w={8}
                                                        h={8}
                                                        color="yellow.400"
                                                    />
                                                ) : (
                                                    <Icon
                                                        as={MdCancel}
                                                        w={8}
                                                        h={8}
                                                        color="red.400"
                                                    />
                                                )}
                                            </Box>
                                            <Box w={8} h={8}>
                                                {!numberBlockState.showButton ? (
                                                    <Icon
                                                        as={MdCheckCircle}
                                                        w={8}
                                                        h={8}
                                                        color="green.400"
                                                    />
                                                ) : (
                                                    <Icon
                                                        as={MdCancel}
                                                        w={8}
                                                        h={8}
                                                        color="red.400"
                                                    />
                                                )}
                                            </Box>
                                            <Box w={8} h={8}>
                                                {!trunkState.showButton &&
                                                !trunkState.showPending ? (
                                                    <Icon
                                                        as={MdCheckCircle}
                                                        w={8}
                                                        h={8}
                                                        color="green.400"
                                                    />
                                                ) : trunkState.showPending ? (
                                                    <Icon
                                                        as={MdPending}
                                                        w={8}
                                                        h={8}
                                                        color="yellow.400"
                                                    />
                                                ) : (
                                                    <Icon
                                                        as={MdCancel}
                                                        w={8}
                                                        h={8}
                                                        color="red.400"
                                                    />
                                                )}
                                            </Box>
                                        </VStack>

                                        <VStack>
                                            <Box h={8} width="100%" pt={1}>
                                                Remove Platform Numbers from{' '}
                                                {userNumberState?.count}{' '}
                                                Platform Users
                                            </Box>
                                            <Box h={8} width="100%" pt={1}>
                                                Remove {numberTrunkState?.count}{' '}
                                                Trunks from Platform Numbers
                                            </Box>
                                            <Box h={8} width="100%" pt={1}>
                                                Remove {numberBlockState?.count}{' '}
                                                Number Blocks from Company
                                            </Box>
                                            <Box h={8} width="100%" pt={1}>
                                                Remove {trunkState?.count}{' '}
                                                Trunks from Company
                                            </Box>
                                        </VStack>
                                    </HStack>
                                    <HStack>
                                        <VStack>
                                            {/* Users */}
                                            <Box
                                                display="flex"
                                                flexDir="row"
                                                alignSelf="flex-end"
                                                h={8}>
                                                {userNumberState.showButton && (
                                                    <>
                                                        <ChakraButton
                                                            mr={1}
                                                            onClick={() =>
                                                                viewNavigate(
                                                                    '/users',
                                                                )
                                                            }
                                                            display="flex"
                                                            size="sm"
                                                            justifyContent="right"
                                                            colorScheme="blue">
                                                            View
                                                        </ChakraButton>
                                                        <ChakraButton
                                                            onClick={
                                                                onDeallocateUsers
                                                            }
                                                            display="flex"
                                                            size="sm"
                                                            isLoading={
                                                                userNumberState.isLoading
                                                            }
                                                            justifyContent="right"
                                                            colorScheme={
                                                                userNumberState.deallocate
                                                                    ? 'red'
                                                                    : 'blackAlpha'
                                                            }
                                                            isDisabled={
                                                                userNumberState.isDisabled
                                                            }>
                                                            Deallocate
                                                        </ChakraButton>
                                                    </>
                                                )}
                                            </Box>
                                            {/* Resource Accounts */}
                                            <Box
                                                display="flex"
                                                flexDir="row"
                                                h={8}
                                                alignSelf="flex-end">
                                                {numberTrunkState.showPending && (
                                                    <Spinner color="brand.500" />
                                                )}
                                                {numberTrunkState.showButton && (
                                                    <>
                                                        <ChakraButton
                                                            mr={1}
                                                            onClick={() =>
                                                                viewNavigate(
                                                                    '/trunks',
                                                                )
                                                            }
                                                            display="flex"
                                                            size="sm"
                                                            justifyContent="right"
                                                            colorScheme="blue">
                                                            View
                                                        </ChakraButton>
                                                        <ChakraButton
                                                            onClick={
                                                                onDeallocateNumberTrunk
                                                            }
                                                            display="flex"
                                                            size="sm"
                                                            isLoading={
                                                                numberTrunkState.isLoading
                                                            }
                                                            justifyContent="right"
                                                            colorScheme={
                                                                numberTrunkState.deallocate
                                                                    ? 'red'
                                                                    : 'blackAlpha'
                                                            }
                                                            isDisabled={
                                                                numberTrunkState.isDisabled
                                                            }>
                                                            Deprovision
                                                        </ChakraButton>
                                                    </>
                                                )}
                                            </Box>
                                            {/* Number blocks */}
                                            <Box h={8} alignSelf="flex-end">
                                                {numberBlockState.showButton && (
                                                    <ChakraButton
                                                        onClick={onDeallocateNB}
                                                        display="flex"
                                                        size="sm"
                                                        isLoading={
                                                            numberBlockState.isLoading
                                                        }
                                                        justifyContent="right"
                                                        colorScheme={
                                                            numberBlockState.deallocate
                                                                ? 'red'
                                                                : 'blackAlpha'
                                                        }
                                                        isDisabled={
                                                            numberBlockState.isDisabled
                                                        }>
                                                        Deallocate
                                                    </ChakraButton>
                                                )}
                                            </Box>
                                            {/* Deprovision trunks */}
                                            <Box
                                                display="flex"
                                                flexDir="row"
                                                h={8}
                                                alignSelf="flex-end">
                                                {trunkState.showPending && (
                                                    <Spinner color="brand.500" />
                                                )}
                                                {trunkState.showButton && (
                                                    <>
                                                        <ChakraButton
                                                            mr={1}
                                                            onClick={() =>
                                                                viewNavigate(
                                                                    '/trunks',
                                                                )
                                                            }
                                                            display="flex"
                                                            size="sm"
                                                            justifyContent="right"
                                                            colorScheme="blue">
                                                            View
                                                        </ChakraButton>
                                                        <ChakraButton
                                                            onClick={
                                                                onDeallocateTrunk
                                                            }
                                                            display="flex"
                                                            size="sm"
                                                            isLoading={
                                                                trunkState.isLoading
                                                            }
                                                            justifyContent="right"
                                                            colorScheme={
                                                                trunkState.deallocate
                                                                    ? 'red'
                                                                    : 'blackAlpha'
                                                            }
                                                            isDisabled={
                                                                trunkState.isDisabled
                                                            }>
                                                            Deprovision
                                                        </ChakraButton>
                                                    </>
                                                )}
                                            </Box>

                                            {/* Deactivate company */}
                                        </VStack>
                                    </HStack>
                                </HStack>
                            </Stack>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <ChakraButton
                            colorScheme="brand"
                            variant="outline"
                            bg={darkMode ? 'white' : null}
                            mr={3}
                            onClick={onClose}>
                            Close
                        </ChakraButton>
                        <ChakraButton
                            isLoading={deleteState.isLoading}
                            colorScheme={
                                deleteState.delete ? 'red' : 'blackAlpha'
                            }
                            onClick={onCompanyDeleteHandler}
                            isDisabled={deleteState.isDisabled}>
                            Delete
                        </ChakraButton>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default EntityDangerzone;
